import { createFileRoute } from "@tanstack/react-router";
import { AbsencePanel } from "@/components/absence/AbsencePanel";
import { ResponseSurfaceBlurb } from "@/components/response/ResponseSurfaceBlurb";
import { EntityScopeChip } from "@/components/search/EntityScopeChip";
import { InstitutionScopeChip } from "@/components/search/InstitutionScopeChip";
import { ModeToggle } from "@/components/search/ModeToggle";
import { SearchBar } from "@/components/search/SearchBar";
import { SearchResults } from "@/components/search/SearchResults";
import {
  Breadcrumb,
  ScopeToggle,
  type ScopeMode,
  SCOPE_MODES,
} from "@/components/shared";

export type PageMode = "search" | "absence";

interface SearchParams {
  q: string;
  mode: PageMode;
  /** Source-scope filter — `all` is the default (no filter), `commons`
   * restricts to the curated public corpus, `org` restricts to commons
   * + the caller's contributions + their org's private docs. Backend
   * support lands with the W4 score-combiner pair-week; until then the
   * param sends but is ignored server-side.
   *
   * Optional in the schema so existing `<Link to="/search">` calls
   * (ModeToggle, SearchBar, the empty-state "browse absence" link)
   * keep compiling without needing a scope prop. Consumers default to
   * `"all"` at read time. */
  scope?: ScopeMode;
  /** Optional entity_id — when present (a finding-aid "Ask about X"
   * navigation), retrieval and the synthesized answer are scoped to that
   * entity's documents server-side. */
  entity?: string;
  /** Optional institution slug — when present (an institution page "Ask
   * about X" navigation), retrieval and the synthesized answer are scoped
   * to that institution's holdings server-side. Mutually exclusive with
   * `entity`. */
  institution?: string;
  /** When `false`, the synthesized-answer blurb is hidden and only the
   * document results show. Set by browse-style entries (a catalog subject
   * card) where the query is a keyword/topic, not a question — so the
   * answer surface (which would read "not in corpus" for a bare topic)
   * never appears. Defaults to shown. */
  answer?: boolean;
}

export const Route = createFileRoute("/search")({
  component: SearchPage,
  validateSearch(raw: Record<string, unknown>): SearchParams {
    const mode = raw.mode === "absence" ? "absence" : "search";
    const scope = SCOPE_MODES.find((s) => s === raw.scope);
    const params: SearchParams = {
      q: typeof raw.q === "string" ? raw.q : "",
      mode,
    };
    if (scope && scope !== "all") params.scope = scope;
    if (typeof raw.entity === "string" && raw.entity) params.entity = raw.entity;
    // Entity takes precedence — never scope to both at once.
    else if (typeof raw.institution === "string" && raw.institution)
      params.institution = raw.institution;
    // Only store the non-default (suppressed) state; accepts boolean or the
    // string form a URL round-trip produces.
    if (raw.answer === false || raw.answer === "false") params.answer = false;
    return params;
  },
});

function SearchPage() {
  const { q, mode, scope = "all", entity, institution, answer } = Route.useSearch();
  const showAnswer = answer !== false;

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8">
      {q && (
        <div className="mb-4">
          <Breadcrumb
            segments={[
              { label: "Corpus", to: "/", search: { mode: "graph" } },
              { label: mode === "absence" ? `Absence · “${q}”` : `“${q}”` },
            ]}
          />
        </div>
      )}
      <SearchBar initialQuery={q} entity={entity} institution={institution} />
      {entity ? (
        <div className="mt-3">
          <EntityScopeChip entityId={entity} query={q} mode={mode} scope={scope} />
        </div>
      ) : (
        institution && (
          <div className="mt-3">
            <InstitutionScopeChip institution={institution} query={q} mode={mode} scope={scope} />
          </div>
        )
      )}
      {q && (
        <>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <ModeToggle query={q} mode={mode} entity={entity} institution={institution} />
            {mode === "search" && (
              <ScopeToggle
                query={q}
                mode={mode}
                scope={scope}
                entity={entity}
                institution={institution}
              />
            )}
          </div>
          <div className="mt-4">
            {mode === "search" ? (
              <>
                {/* Synthesized-answer blurb above the results. Fetches in
                    parallel with the search itself and silently hides on
                    error so it never disturbs the result list. Suppressed for
                    browse-style entries (catalog subject cards), where a bare
                    topic isn't a question and the answer surface would read
                    "not in corpus". */}
                {showAnswer && (
                  <ResponseSurfaceBlurb query={q} entity={entity} institution={institution} />
                )}
                <SearchResults query={q} scope={scope} entity={entity} institution={institution} />
              </>
            ) : (
              <AbsencePanel query={q} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
